/**
 * @sazeo/sdk — Billing hooks
 *
 * TanStack Query wrappers for provider-neutral billing. Georgia launch uses Bank
 * of Georgia Edge Functions for payment orders while legacy Stripe hooks remain
 * exported for existing tenant settings surfaces. All Edge Function calls use
 * the user JWT. No service_role.
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const billingKeys = {
  all: ['billing'] as const,
  subscription: (tenantId: string) => [...billingKeys.all, 'subscription', tenantId] as const,
  invoices: (tenantId: string) => [...billingKeys.all, 'invoices', tenantId] as const,
  companyProfile: (tenantId: string) => [...billingKeys.all, 'company-profile', tenantId] as const,
  hrInvoices: (tenantId: string) => [...billingKeys.all, 'hr-invoices', tenantId] as const,
  hrTransactions: (tenantId: string) => [...billingKeys.all, 'hr-transactions', tenantId] as const,
  paymentStatus: (input: BogPaymentStatusInput) =>
    [
      ...billingKeys.all,
      'bog-payment-status',
      input.id ?? null,
      input.providerOrderId ?? null,
      input.reconcile ?? false,
    ] as const,
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubscriptionRow {
  id: string;
  tenant_id: string;
  plan: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'past_due' | 'canceled' | 'trialing';
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  seat_count: number;
  created_at: string;
  updated_at: string;
}

export interface InvoiceRow {
  id: string;
  tenant_id: string;
  stripe_invoice_id: string | null;
  amount_total_cents: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  pdf_url: string | null;
  due_at: string | null;
  paid_at: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
}

export interface HrCompanyBillingProfileRow {
  id: string;
  tenant_id: string;
  payg_balance: number | string;
  own_api_enabled: boolean;
  managed_api_enabled: boolean;
  subscription_status:
    | 'trial_active'
    | 'trial_expired'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'suspended';
  trial_started_at: string | null;
  trial_ends_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface HrInvoiceRow {
  id: string;
  tenant_id: string;
  number: string;
  status: 'draft' | 'ready' | 'issued' | 'paid' | 'void' | 'failed';
  invoice_type: 'usage' | 'subscription' | 'adjustment' | 'manual';
  currency: string;
  subtotal: number | string;
  vat_amount: number | string;
  total: number | string;
  due_at: string | null;
  issued_at: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface HrBillingTransactionRow {
  id: string;
  tenant_id: string;
  user_id: string | null;
  transaction_type:
    | 'top_up'
    | 'ai_charge'
    | 'service_fee'
    | 'refund'
    | 'adjustment'
    | 'subscription_fee';
  amount: number | string;
  currency: string;
  status: 'pending' | 'approved' | 'completed' | 'failed' | 'void';
  provider: string | null;
  provider_transaction_id: string | null;
  created_at: string;
}

export type BogPaymentPurpose =
  | 'company_top_up'
  | 'subscription'
  | 'candidate_precheck'
  | 'candidate_tailoring';
export type CandidatePremiumPaymentProduct = 'precheck_single' | 'precheck_triple' | 'tailoring';

export type BogPaymentStatus =
  | 'created'
  | 'processing'
  | 'completed'
  | 'rejected'
  | 'refunded'
  | 'refunded_partially';

export interface BogPaymentInitInput {
  amountMinor: number;
  candidatePremium?: {
    product: CandidatePremiumPaymentProduct;
    sourceJobSlug?: string | null;
  };
  currency?: 'GEL' | 'USD' | 'EUR' | 'GBP';
  description?: string;
  failUrl: string;
  purpose: BogPaymentPurpose;
  successUrl: string;
}

export interface BogPaymentInitResult {
  details_url: string | null;
  external_order_id: string;
  id: string;
  provider: 'bog';
  provider_order_id: string;
  redirect_url: string;
  status: BogPaymentStatus;
}

export interface BogPaymentStatusInput {
  id?: string;
  providerOrderId?: string;
  reconcile?: boolean;
}

export interface BogPaymentStatusResult {
  id: string;
  provider: 'bog';
  provider_order_id: string | null;
  external_order_id: string;
  purpose: BogPaymentPurpose;
  amount_minor: number;
  currency: string;
  status: BogPaymentStatus;
  redirect_url: string | null;
  details_url: string | null;
  completed_at: string | null;
  reconciled: boolean;
  provider_status: string | null;
}

export class BillingApiError extends Error {
  status: number;
  code?: string;
  requestId?: string;

  constructor(message: string, status: number, code?: string, requestId?: string) {
    super(message);
    this.name = 'BillingApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

type BillingSupabaseClient = SupabaseClientLike | null | undefined;

function makeRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolveClient(supabase: BillingSupabaseClient): SupabaseClientLike {
  if (supabase) return supabase;
  return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseClientLike;
}

async function getAuthHeaders(supabase: BillingSupabaseClient): Promise<Record<string, string>> {
  const { data } = await resolveClient(supabase).auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: BillingSupabaseClient,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const authHeaders = await getAuthHeaders(supabase);
  const headers = new Headers(init?.headers);
  headers.set('content-type', headers.get('content-type') ?? 'application/json');
  headers.set('x-request-id', headers.get('x-request-id') ?? makeRequestId());
  for (const [key, value] of Object.entries(authHeaders)) {
    headers.set(key, value);
  }
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { code?: string; message?: string; request_id?: string };
    };
    throw new BillingApiError(
      body.error?.message ?? res.statusText,
      res.status,
      body.error?.code,
      res.headers.get('x-request-id') ?? body.error?.request_id ?? undefined,
    );
  }
  return res.json() as Promise<T>;
}

function rememberBogPaymentOrderId(orderId: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem('sazeo:last_bog_payment_order_id', orderId);
  } catch {
    // Browser storage can be unavailable; the Edge success URL also carries the id.
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * useSubscription — GET subscription row from DB via Supabase client.
 * Uses RLS — tenant_id scoped automatically via user JWT.
 */
export function useSubscription(supabase: BillingSupabaseClient, tenantId: string) {
  return useQuery({
    queryKey: billingKeys.subscription(tenantId),
    queryFn: async () => {
      // Narrow cast: supabase.from exists on the full client used at runtime.
      type SubscriptionDbClient = {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (
              col: string,
              val: string,
            ) => {
              maybeSingle: () => Promise<{
                data: SubscriptionRow | null;
                error: { message: string } | null;
              }>;
            };
          };
        };
      };
      const { data, error } = await (resolveClient(supabase) as unknown as SubscriptionDbClient)
        .from('subscriptions')
        .select(
          'id, tenant_id, plan, status, stripe_subscription_id, stripe_customer_id, ' +
            'current_period_start, current_period_end, cancel_at_period_end, seat_count, created_at, updated_at',
        )
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

/**
 * useBillingInvoices — reads platform subscription invoices (RLS-scoped to tenant).
 */
export function useBillingInvoices(supabase: BillingSupabaseClient, tenantId: string) {
  return useQuery({
    queryKey: billingKeys.invoices(tenantId),
    queryFn: async () => {
      type InvoicesDbClient = {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (
              col: string,
              val: string,
            ) => {
              order: (
                col: string,
                opts: { ascending: boolean },
              ) => Promise<{ data: InvoiceRow[] | null; error: { message: string } | null }>;
            };
          };
        };
      };
      const { data, error } = await (resolveClient(supabase) as unknown as InvoicesDbClient)
        .from('invoices')
        .select(
          'id, tenant_id, stripe_invoice_id, amount_total_cents, currency, status, ' +
            'pdf_url, due_at, paid_at, period_start, period_end, created_at',
        )
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as InvoiceRow[];
    },
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

/**
 * useHrCompanyBillingProfile — reads StepUp HR billing profile from
 * hr_company_profiles. This is the BOG/Pay-as-you-go launch balance source.
 */
export function useHrCompanyBillingProfile(supabase: BillingSupabaseClient, tenantId: string) {
  return useQuery({
    queryKey: billingKeys.companyProfile(tenantId),
    queryFn: async () => {
      type ProfileDbClient = {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (
              col: string,
              val: string,
            ) => {
              maybeSingle: () => Promise<{
                data: HrCompanyBillingProfileRow | null;
                error: { message: string } | null;
              }>;
            };
          };
        };
      };
      const { data, error } = await (resolveClient(supabase) as unknown as ProfileDbClient)
        .from('hr_company_profiles')
        .select(
          'id, tenant_id, payg_balance, own_api_enabled, managed_api_enabled, subscription_status, ' +
            'trial_started_at, trial_ends_at, created_at, updated_at',
        )
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

/** Reads paid/provider-neutral HR invoices created by BOG callback effects. */
export function useHrBillingInvoices(supabase: BillingSupabaseClient, tenantId: string) {
  return useQuery({
    queryKey: billingKeys.hrInvoices(tenantId),
    queryFn: async () => {
      type InvoicesDbClient = {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (
              col: string,
              val: string,
            ) => {
              order: (
                col: string,
                opts: { ascending: boolean },
              ) => Promise<{ data: HrInvoiceRow[] | null; error: { message: string } | null }>;
            };
          };
        };
      };
      const { data, error } = await (resolveClient(supabase) as unknown as InvoicesDbClient)
        .from('hr_invoices')
        .select(
          'id, tenant_id, number, status, invoice_type, currency, subtotal, vat_amount, total, due_at, issued_at, paid_at, created_at',
        )
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

/** Reads HR billing ledger transactions produced by BOG callbacks and AI charges. */
export function useHrBillingTransactions(supabase: BillingSupabaseClient, tenantId: string) {
  return useQuery({
    queryKey: billingKeys.hrTransactions(tenantId),
    queryFn: async () => {
      type TransactionsDbClient = {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (
              col: string,
              val: string,
            ) => {
              order: (
                col: string,
                opts: { ascending: boolean },
              ) => Promise<{
                data: HrBillingTransactionRow[] | null;
                error: { message: string } | null;
              }>;
            };
          };
        };
      };
      const { data, error } = await (resolveClient(supabase) as unknown as TransactionsDbClient)
        .from('hr_billing_transactions')
        .select(
          'id, tenant_id, user_id, transaction_type, amount, currency, status, provider, provider_transaction_id, created_at',
        )
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

/**
 * useUpgradePlan — POST /billing-checkout → Stripe Checkout session URL.
 * On success, redirects the browser to the Stripe-hosted checkout page.
 */
export function useUpgradePlan(supabase: BillingSupabaseClient) {
  return useMutation({
    mutationFn: (payload: { plan: 'pro' | 'enterprise'; interval: 'monthly' | 'annual' }) =>
      apiFetch<{ url: string }>(supabase, 'billing-checkout', {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((r) => r.url),
    onSuccess: (url) => {
      window.location.href = url;
    },
  });
}

/** Alias kept for backward compat with existing BillingSettings.tsx imports. */
export const useCheckout = useUpgradePlan;

/**
 * useBogPaymentInit — POST /bog-payment-init and redirect to Bank of Georgia.
 */
export function useBogPaymentInit(supabase: BillingSupabaseClient) {
  return useMutation({
    mutationFn: (payload: BogPaymentInitInput) =>
      apiFetch<BogPaymentInitResult>(supabase, 'bog-payment-init', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (order) => {
      rememberBogPaymentOrderId(order.id);
      window.location.href = order.redirect_url;
    },
  });
}

/**
 * useBogPaymentStatus — GET local BOG payment status. Pass reconcile=true only
 * for explicit post-redirect refreshes because it calls BOG details.
 */
export function useBogPaymentStatus(supabase: BillingSupabaseClient, input: BogPaymentStatusInput) {
  const hasLookup = Boolean(input.id || input.providerOrderId);
  return useQuery({
    queryKey: billingKeys.paymentStatus(input),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (input.id) params.set('id', input.id);
      if (input.providerOrderId) params.set('provider_order_id', input.providerOrderId);
      if (input.reconcile) params.set('reconcile', 'true');
      return apiFetch<BogPaymentStatusResult>(supabase, `bog-payment-status?${params.toString()}`);
    },
    enabled: hasLookup,
    staleTime: input.reconcile ? 0 : 15_000,
  });
}

/**
 * useCancelSubscription — POST /billing-cancel
 * Sets cancel_at_period_end=true on the active subscription.
 */
export function useCancelSubscription(supabase: BillingSupabaseClient, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>(supabase, 'billing-cancel', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingKeys.subscription(tenantId) });
    },
  });
}

/**
 * useBillingPortal — POST /billing-portal → Stripe Customer Portal session URL.
 * On success, opens the portal in a new tab (so the user stays in-app).
 */
export function useBillingPortal(supabase: BillingSupabaseClient) {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ url: string }>(supabase, 'billing-portal', { method: 'POST' }).then((r) => r.url),
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  });
}

// ---------------------------------------------------------------------------
// Invoice items (v2 billing layer) — types
// ---------------------------------------------------------------------------

export interface InvoiceItemRow {
  id: string;
  invoice_id: string;
  tenant_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  created_at: string;
}

export interface CreateInvoiceItemInput {
  description: string;
  quantity: number;
  unit_price: number;
}

export interface UpdateInvoiceItemInput extends Partial<CreateInvoiceItemInput> {
  id: string;
}

export interface ReconciliationRow {
  id: string;
  tenant_id: string;
  reference: string | null;
  total_amount: number;
  currency: string;
  status: string;
  invoice_date: string;
  items_total: number;
  reconciliation_status: 'matched' | 'unpaid' | 'partial';
}

export interface ReconciliationFilters {
  status?: string;
  from?: string;
  to?: string;
}

// ---------------------------------------------------------------------------
// Invoice items query key extension
// ---------------------------------------------------------------------------

// Extend billingKeys at runtime — we can't mutate the const object, so define
// separate key factories for the new entities.
export const invoiceItemKeys = {
  list: (tenantId: string, invoiceId: string) =>
    ['billing', 'invoice-items', tenantId, invoiceId] as const,
  reconciliation: (tenantId: string, filters?: ReconciliationFilters) =>
    ['billing', 'reconciliation', tenantId, filters ?? null] as const,
};

// ---------------------------------------------------------------------------
// useInvoiceItems
// ---------------------------------------------------------------------------

type ItemsDbClient = {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (
        col: string,
        val: string,
      ) => {
        eq: (
          col: string,
          val: string,
        ) => {
          order: (
            col: string,
            opts: { ascending: boolean },
          ) => Promise<{ data: InvoiceItemRow[] | null; error: { message: string } | null }>;
        };
      };
    };
  };
};

export function useInvoiceItems(
  supabase: BillingSupabaseClient,
  tenantId: string,
  invoiceId: string,
) {
  return useQuery({
    queryKey: invoiceItemKeys.list(tenantId, invoiceId),
    queryFn: async () => {
      const { data, error } = await (resolveClient(supabase) as unknown as ItemsDbClient)
        .from('invoice_items')
        .select('id, invoice_id, tenant_id, description, quantity, unit_price, amount, created_at')
        .eq('tenant_id', tenantId)
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as InvoiceItemRow[];
    },
    enabled: Boolean(tenantId) && Boolean(invoiceId),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// useCreateInvoiceItem
// ---------------------------------------------------------------------------

type InsertDbClient = {
  from: (table: string) => {
    insert: (row: object) => {
      select: (cols: string) => {
        single: () => Promise<{ data: InvoiceItemRow | null; error: { message: string } | null }>;
      };
    };
  };
};

export function useCreateInvoiceItem(supabase: BillingSupabaseClient, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      invoiceId,
      input,
    }: {
      invoiceId: string;
      input: CreateInvoiceItemInput;
    }) => {
      const { data, error } = await (resolveClient(supabase) as unknown as InsertDbClient)
        .from('invoice_items')
        .insert({ tenant_id: tenantId, invoice_id: invoiceId, ...input })
        .select('id, invoice_id, tenant_id, description, quantity, unit_price, amount, created_at')
        .single();
      if (error) throw new Error(error.message);
      return data as InvoiceItemRow;
    },
    onSuccess: (_data, { invoiceId }) => {
      void qc.invalidateQueries({ queryKey: invoiceItemKeys.list(tenantId, invoiceId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateInvoiceItem
// ---------------------------------------------------------------------------

type UpdateDbClient = {
  from: (table: string) => {
    update: (patch: object) => {
      eq: (
        col: string,
        val: string,
      ) => {
        eq: (
          col: string,
          val: string,
        ) => {
          select: (cols: string) => {
            single: () => Promise<{
              data: InvoiceItemRow | null;
              error: { message: string } | null;
            }>;
          };
        };
      };
    };
  };
};

export function useUpdateInvoiceItem(supabase: BillingSupabaseClient, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      input,
    }: {
      invoiceId: string;
      input: UpdateInvoiceItemInput;
    }) => {
      const { id, ...patch } = input;
      const { data, error } = await (resolveClient(supabase) as unknown as UpdateDbClient)
        .from('invoice_items')
        .update(patch)
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select('id, invoice_id, tenant_id, description, quantity, unit_price, amount, created_at')
        .single();
      if (error) throw new Error(error.message);
      return data as InvoiceItemRow;
    },
    onSuccess: (_data, { invoiceId }) => {
      void qc.invalidateQueries({ queryKey: invoiceItemKeys.list(tenantId, invoiceId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteInvoiceItem
// ---------------------------------------------------------------------------

type DeleteDbClient = {
  from: (table: string) => {
    delete: () => {
      eq: (
        col: string,
        val: string,
      ) => {
        eq: (col: string, val: string) => Promise<{ error: { message: string } | null }>;
      };
    };
  };
};

export function useDeleteInvoiceItem(supabase: BillingSupabaseClient, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string; invoiceId: string }) => {
      const { error } = await (resolveClient(supabase) as unknown as DeleteDbClient)
        .from('invoice_items')
        .delete()
        .eq('id', itemId)
        .eq('tenant_id', tenantId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { invoiceId }) => {
      void qc.invalidateQueries({ queryKey: invoiceItemKeys.list(tenantId, invoiceId) });
    },
  });
}

// ---------------------------------------------------------------------------
// useInvoiceReconciliation
// ---------------------------------------------------------------------------

// The reconciliation view does not have date/status columns that need server-side
// filtering beyond tenant isolation (which RLS handles). Client-side filter is
// acceptable for the small cardinality of invoice lists.
type ReconSimpleDbClient = {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (
        col: string,
        val: string,
      ) => Promise<{ data: ReconciliationRow[] | null; error: { message: string } | null }>;
    };
  };
};

export function useInvoiceReconciliation(
  supabase: BillingSupabaseClient,
  tenantId: string,
  filters?: ReconciliationFilters,
) {
  return useQuery({
    queryKey: invoiceItemKeys.reconciliation(tenantId, filters),
    queryFn: async () => {
      const { data, error } = await (resolveClient(supabase) as unknown as ReconSimpleDbClient)
        .from('invoice_reconciliation')
        .select(
          'id, tenant_id, reference, total_amount, currency, status, invoice_date, items_total, reconciliation_status',
        )
        .eq('tenant_id', tenantId);
      if (error) throw new Error(error.message);
      let rows = (data ?? []) as ReconciliationRow[];
      if (filters?.status && filters.status !== 'all') {
        rows = rows.filter((r) => r.reconciliation_status === filters.status);
      }
      if (filters?.from) {
        rows = rows.filter((r) => r.invoice_date >= filters.from!);
      }
      if (filters?.to) {
        rows = rows.filter((r) => r.invoice_date <= filters.to!);
      }
      return rows;
    },
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}
